import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import DateInput from './DateInput';

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const qty = (v) => (v == null ? '' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 }));
const num = (v) => Number(v || 0);

/**
 * Inventory > Stock Ageing: what stock is worth and how long it has been sitting, by item and age band, with the items
 * that haven't gone out for a while marked slow-moving. Each item opens to its lots. Managers only (it shows values).
 */
// A lot's expiry: red once past, amber within a month, plain otherwise
function ExpiryLabel({ date, days }) {
  if (days < 0) return <Badge bg="danger" className="fw-normal">Expired {day(date)}</Badge>;
  if (days <= 30) return <Badge bg="warning" text="dark" className="fw-normal">{day(date)} ({days} d)</Badge>;
  return <span>{day(date)}</span>;
}

function StockAgeing({ active, currency = 'INR' }) {
  const [location, setLocation] = useState('');
  const [slowDays, setSlowDays] = useState(90);
  const [onlySlow, setOnlySlow] = useState(false);
  const [filter, setFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});
  const { expiryEnabled } = useAuth();
  // The lot whose expiry date is being set: { lotId, name, date }
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 });
    return (v) => f.format(num(v));
  }, [currency]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/api/inventory/stock-ageing', { params: { location: location || undefined, slowDays } })
      .then((r) => setData(r.data))
      .catch((error) => toast.error(errorMessage(error, 'Could not load stock ageing')))
      .finally(() => setLoading(false));
  }, [location, slowDays]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const items = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (data?.items || []).filter((i) => (!onlySlow || i.slowMoving) && (!f || i.itemName.toLowerCase().includes(f)));
  }, [data, onlySlow, filter]);

  if (!active) return null;

  const download = () => {
    const header = ['Item', 'Unit', 'Quantity', 'Value', ...(data.bands || []), 'Oldest (days)', 'Last went out', 'Days idle', 'Slow-moving'];
    const rows = items.map((i) => [i.itemName, i.unit || '', i.quantity, num(i.value).toFixed(2), ...i.bands.map((b) => num(b).toFixed(2)),
      i.oldestDays, i.lastOutOn || 'never', i.daysIdle, i.slowMoving ? 'yes' : 'no']);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-ageing-${data.asOf}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const total = num(data?.total);
  // Expiry shows only when some lot on hand has a date (the organization keeps them)
  const hasExpiry = !!data && (expiryEnabled || data.items.some((i) => i.nearestExpiry));

  const saveExpiry = async (date) => {
    setSaving(true);
    try {
      await axios.put(`/api/inventory/stock-ageing/lots/${editing.lotId}/expiry`, { expiryDate: date || null });
      setEditing(null);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the expiry date'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-muted">What your stock is worth and how long it has been sitting, lot by lot at cost. Money in old stock is money not working.</p>
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <Form.Group>
          <Form.Label className="small mb-1">Location</Form.Label>
          <Form.Select size="sm" value={location} onChange={(e) => setLocation(e.target.value)}>
            <option value="">All locations</option>
            {(data?.locations || []).map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label className="small mb-1">Slow-moving after</Form.Label>
          <Form.Select size="sm" value={slowDays} onChange={(e) => setSlowDays(Number(e.target.value))}>
            {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days without going out</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Control size="sm" style={{ maxWidth: 220 }} placeholder="Find an item" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <Form.Check type="switch" id="only-slow" label="Slow-moving only" checked={onlySlow} onChange={(e) => setOnlySlow(e.target.checked)} />
        {data && items.length > 0 && <Button size="sm" variant="outline-secondary" className="ms-auto" onClick={download}><i className="bi bi-download me-1" />CSV</Button>}
      </div>

      {loading && !data && <div className="text-center py-4"><Spinner animation="border" /></div>}
      {data && (
        <>
          <Row className="g-3 mb-3">
            <Col md={3}>
              <Card className="h-100"><Card.Body className="py-3">
                <div className="small text-muted text-uppercase">Stock value</div>
                <div className="fs-4 fw-semibold">{money(total)}</div>
                <div className="small text-muted">{data.items.length} items · as on {day(data.asOf)}</div>
              </Card.Body></Card>
            </Col>
            <Col md={3}>
              <Card className="h-100"><Card.Body className="py-3">
                <div className="small text-muted text-uppercase">Slow-moving</div>
                <div className={`fs-4 fw-semibold ${num(data.slowMovingValue) > 0 ? 'text-danger' : ''}`}>{money(data.slowMovingValue)}</div>
                <div className="small text-muted">{data.slowMovingItems} item{data.slowMovingItems === 1 ? '' : 's'} not gone out in {data.slowDays} days</div>
              </Card.Body></Card>
            </Col>
            <Col md={6}>
              <Card className="h-100"><Card.Body className="py-3">
                <div className="small text-muted text-uppercase mb-2">By age</div>
                <div className="d-flex rounded overflow-hidden mb-2" style={{ height: 10, background: 'var(--bs-secondary-bg)' }}>
                  {data.bandTotals.map((v, i) => (total > 0 && num(v) > 0 ? (
                    <div key={i} title={`${data.bands[i]}: ${money(v)}`}
                      style={{ width: `${(num(v) / total) * 100}%`, background: `rgba(220, 53, 69, ${0.15 + i * 0.2})` }} />
                  ) : null))}
                </div>
                <div className="d-flex flex-wrap gap-3 small">
                  {data.bands.map((b, i) => <span key={b}><span className="text-muted">{b}</span> {money(data.bandTotals[i])}</span>)}
                </div>
              </Card.Body></Card>
            </Col>
          </Row>

          {hasExpiry && (
            <Row className="g-3 mb-3">
              <Col md={3}>
                <Card className="h-100"><Card.Body className="py-3">
                  <div className="small text-muted text-uppercase">Expired</div>
                  <div className={`fs-4 fw-semibold ${data.expiredLots > 0 ? 'text-danger' : ''}`}>{money(data.expiredValue)}</div>
                  <div className="small text-muted">{data.expiredLots} lot{data.expiredLots === 1 ? '' : 's'} past their expiry date</div>
                </Card.Body></Card>
              </Col>
              <Col md={3}>
                <Card className="h-100"><Card.Body className="py-3">
                  <div className="small text-muted text-uppercase">Expiring soon</div>
                  <div className={`fs-4 fw-semibold ${data.expiringLots > 0 ? 'text-warning' : ''}`}>{money(data.expiringValue)}</div>
                  <div className="small text-muted">{data.expiringLots} lot{data.expiringLots === 1 ? '' : 's'} within {data.expirySoonDays} days</div>
                </Card.Body></Card>
              </Col>
              {data.undatedLots > 0 && (
                <Col md={6} className="d-flex align-items-center small text-muted">
                  <span><i className="bi bi-info-circle me-1" />{data.undatedLots} lot{data.undatedLots === 1 ? ' has' : 's have'} no expiry date, so {data.undatedLots === 1 ? 'it is' : 'they are'} not
                    tracked. Open an item and press Set date on a lot.</span>
                </Col>
              )}
            </Row>
          )}
          <Table bordered hover responsive size="sm" className="small">
            <thead>
              <tr>
                <th>Item</th><th className="text-end">Quantity</th><th className="text-end">Value</th>
                {data.bands.map((b) => <th key={b} className="text-end text-nowrap">{b}</th>)}
                <th className="text-end">Oldest</th><th>Last went out</th>{hasExpiry && <th>Expires</th>}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5 + data.bands.length + (hasExpiry ? 1 : 0)} className="text-center text-muted py-3">{data.items.length ? 'Nothing matches.' : 'No stock on hand.'}</td></tr>}
              {items.map((i) => (
                <Fragment key={i.itemId}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setOpen({ ...open, [i.itemId]: !open[i.itemId] })}>
                    <td>
                      <i className={`bi bi-chevron-${open[i.itemId] ? 'down' : 'right'} me-1 text-muted`} />
                      {i.itemName}
                      {i.slowMoving && <Badge bg="danger" className="ms-2 fw-normal">Slow</Badge>}
                    </td>
                    <td className="text-end text-nowrap">{qty(i.quantity)} {i.unit || ''}</td>
                    <td className="text-end text-nowrap fw-semibold">{money(i.value)}</td>
                    {i.bands.map((b, k) => <td key={k} className="text-end text-nowrap">{num(b) > 0 ? money(b) : ''}</td>)}
                    <td className="text-end text-nowrap">{i.oldestDays} d</td>
                    <td className="text-nowrap">{i.lastOutOn ? `${day(i.lastOutOn)} (${i.daysIdle} d)` : <span className="text-muted">Never</span>}</td>
                    {hasExpiry && <td className="text-nowrap">{i.nearestExpiry ? <ExpiryLabel date={i.nearestExpiry} days={Math.round((new Date(`${i.nearestExpiry}T00:00:00`) - new Date(`${data.asOf}T00:00:00`)) / 86400000)} /> : ''}</td>}
                  </tr>
                  {open[i.itemId] && i.lots.map((l, k) => (
                    <tr key={`${i.itemId}-${k}`} className="text-muted">
                      <td className="ps-4">
                        Lot <span className="font-monospace">{l.lotNumber}</span> · {l.location} · received {day(l.receivedOn)}
                      </td>
                      <td className="text-end text-nowrap">{qty(l.quantity)} {i.unit || ''}</td>
                      <td className="text-end text-nowrap">{money(l.value)}</td>
                      <td colSpan={data.bands.length} className="small">at {money(l.unitCost)} a unit</td>
                      <td className="text-end text-nowrap">{l.ageDays} d</td>
                      <td />
                      {hasExpiry && (
                        <td className="text-nowrap">
                          {l.expiryDate ? <ExpiryLabel date={l.expiryDate} days={l.daysToExpiry} /> : null}
                          {expiryEnabled && (
                            <Button size="sm" variant="link" className="p-0 ms-2 align-baseline"
                              onClick={() => setEditing({ lotId: l.lotId, name: `${i.itemName}, lot ${l.lotNumber}`, date: l.expiryDate || '' })}>
                              {l.expiryDate ? 'Change' : 'Set date'}
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </Table>
          <p className="small text-muted">
            Values are each lot&apos;s cost (purchase price plus freight and other costs on its receipt), the same as the closing
            stock in your Profit &amp; Loss. Stock written off doesn&apos;t count as going out.
          </p>
        </>
      )}
      <Modal show={!!editing} onHide={() => setEditing(null)} size="sm">
        <Modal.Header closeButton><Modal.Title as="h6">Expiry date</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-2">{editing?.name}</p>
          <DateInput value={editing?.date || ''} onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
        </Modal.Body>
        <Modal.Footer>
          {editing?.date && <Button variant="outline-danger" size="sm" disabled={saving} onClick={() => saveExpiry('')}>Remove date</Button>}
          <Button variant="secondary" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
          <Button size="sm" disabled={saving || !editing?.date} onClick={() => saveExpiry(editing.date)}>{saving ? 'Saving...' : 'Save'}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default StockAgeing;
